/**
 * Site-wide structured data: the one Organization and the one WebSite of cowork24.
 *
 * Decision D8 of the SEO overhaul: both nodes carry a fixed `@id`
 * (`<root>/#organization`, `<root>/#website`) and are emitted in full only on the home page.
 * Every other page refers to them by `@id` (`organizationRef`, `websiteRef`) instead of describing
 * the company again. Before this, every page emitted its own anonymous Organization without a logo,
 * and /support and /p/space_owner each added a second and a third one with different facts, which
 * the validator reported as "Organization described 2 different ways".
 *
 * There is deliberately no SearchAction on the WebSite: Google retired the sitelinks search box in
 * 2024, and it crawled the literal template `/s?keywords={search_term_string}` as a page (Search
 * Console, soft 404 and "crawled – not indexed").
 *
 * The facts below are the operator's legal identity as printed on /support, /privacy-policy and
 * /terms-of-service. Change them there and here together.
 */
import { SUPPORT_EMAIL } from './mailto';

export const SITE_LANGUAGE = 'de-CH';

export const ORGANIZATION_FACTS = {
  alternateName: 'cowork24.ch',
  legalName: 'Pinnacle Interactive GmbH',
  email: SUPPORT_EMAIL,
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Herzogstrasse 10',
    postalCode: '5000',
    addressLocality: 'Aarau',
    addressCountry: 'CH',
  },
};

/**
 * @param {string} marketplaceRootURL e.g. 'https://www.cowork24.ch' (with or without slash)
 * @returns {string} the root without trailing slash
 */
export const siteRootUrl = marketplaceRootURL => `${marketplaceRootURL || ''}`.replace(/\/+$/, '');

/** The home page URL as Google wants it for site names: with the trailing slash. */
export const homePageUrl = marketplaceRootURL => `${siteRootUrl(marketplaceRootURL)}/`;

export const organizationId = marketplaceRootURL =>
  `${siteRootUrl(marketplaceRootURL)}/#organization`;
export const websiteId = marketplaceRootURL => `${siteRootUrl(marketplaceRootURL)}/#website`;

/** `{ '@id': '<root>/#organization' }` — how every page other than home points at the company. */
export const organizationRef = marketplaceRootURL => ({
  '@id': organizationId(marketplaceRootURL),
});
export const websiteRef = marketplaceRootURL => ({ '@id': websiteId(marketplaceRootURL) });

/**
 * A named reference, for places where a bare `@id` is not enough because the node is not on the
 * page (e.g. Offer.seller on a listing page: Google does not follow `@id`s across pages).
 *
 * @param {Object} config marketplace config
 * @returns {Object} Organization node with @id, name and url only
 */
export const organizationReferenceNode = config => ({
  '@type': 'Organization',
  '@id': organizationId(config.marketplaceRootURL),
  name: config.marketplaceName,
  url: homePageUrl(config.marketplaceRootURL),
});

/** True for the canonical path of the home page, where the full nodes belong. */
export const isHomeCanonicalPath = canonicalPath => canonicalPath === '/';

const twitterPageURL = siteTwitterHandle => {
  if (!siteTwitterHandle) {
    return null;
  }
  const handle =
    siteTwitterHandle.charAt(0) === '@' ? siteTwitterHandle.substring(1) : siteTwitterHandle;
  return `https://twitter.com/${handle}`;
};

/**
 * The company's own profiles elsewhere (schema.org sameAs). Only real, clean URLs — the hosted
 * footer links carry tracking parameters, which is why these come from config.
 *
 * @param {Object} config marketplace config
 * @returns {Array<string>}
 */
export const organizationSameAs = config =>
  [
    config.siteLinkedInPage,
    config.siteInstagramPage,
    config.siteFacebookPage,
    twitterPageURL(config.siteTwitterHandle),
  ].filter(url => typeof url === 'string' && url.length > 0);

/**
 * The square app icon is the logo: Google wants a crawlable image of at least 112×112, and the
 * hosted appIcon (white wordmark on coral) comes in square variants up to 512×512 — the same asset
 * the apple-touch-icon and the web manifest use.
 *
 * @param {Object} appIcon config.branding.appIcon (hosted imageAsset)
 * @returns {Object|null} ImageObject of the largest square variant, or null
 */
export const logoFromAppIcon = appIcon => {
  const variants =
    appIcon?.type === 'imageAsset' ? Object.values(appIcon?.attributes?.variants || {}) : [];
  const largestSquare = variants
    .filter(v => v?.url && v.width > 0 && v.width === v.height)
    .sort((a, b) => b.width - a.width)[0];
  return largestSquare
    ? {
        '@type': 'ImageObject',
        url: largestSquare.url,
        width: largestSquare.width,
        height: largestSquare.height,
      }
    : null;
};

/**
 * @param {Object} config marketplace config
 * @param {Object} [options]
 * @param {string} [options.description] what cowork24 is, one sentence
 * @returns {Object} the full Organization node
 */
export const buildOrganizationSchema = (config, options = {}) => {
  const { description } = options;
  const root = config.marketplaceRootURL;
  const logo = logoFromAppIcon(config.branding?.appIcon);
  const name = config.marketplaceName;
  const alternateNameMaybe =
    ORGANIZATION_FACTS.alternateName && ORGANIZATION_FACTS.alternateName !== name
      ? { alternateName: ORGANIZATION_FACTS.alternateName }
      : {};

  return {
    '@type': ['Organization', 'OnlineBusiness'],
    '@id': organizationId(root),
    name,
    ...alternateNameMaybe,
    legalName: ORGANIZATION_FACTS.legalName,
    url: homePageUrl(root),
    ...(logo ? { logo } : {}),
    ...(description ? { description } : {}),
    email: ORGANIZATION_FACTS.email,
    address: ORGANIZATION_FACTS.address,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: ORGANIZATION_FACTS.email,
        availableLanguage: ['de', 'en'],
      },
    ],
    areaServed: { '@type': 'Country', name: 'Schweiz' },
    sameAs: organizationSameAs(config),
  };
};

/**
 * @param {Object} config marketplace config
 * @param {Object} [options]
 * @param {string} [options.description] the site's default description
 * @returns {Object} the WebSite node (no SearchAction, see the module comment)
 */
export const buildWebSiteSchema = (config, options = {}) => {
  const { description } = options;
  const root = config.marketplaceRootURL;
  const name = config.marketplaceName;
  const alternateNames = [ORGANIZATION_FACTS.alternateName].filter(n => n && n !== name);

  return {
    '@type': 'WebSite',
    '@id': websiteId(root),
    url: homePageUrl(root),
    name,
    ...(alternateNames.length > 0 ? { alternateName: alternateNames } : {}),
    ...(description ? { description } : {}),
    inLanguage: SITE_LANGUAGE,
    publisher: organizationRef(root),
  };
};

/**
 * Both site nodes, for the home page.
 *
 * @param {Object} config marketplace config
 * @param {Object} descriptions
 * @param {string} descriptions.organizationDescription
 * @param {string} descriptions.websiteDescription
 * @returns {Array<Object>} [Organization, WebSite]
 */
export const buildSiteSchemas = (config, descriptions = {}) => [
  buildOrganizationSchema(config, { description: descriptions.organizationDescription }),
  buildWebSiteSchema(config, { description: descriptions.websiteDescription }),
];
